import { useEffect, useMemo, useRef } from 'react';

import {
  AuthWithBiometryV5ProcessProvider,
  LoadingPlaceholderShared,
  getSignInTabInitialState,
  useLoginV5ViewController,
  useAuthWithBiometryV5Process,
} from '@liscio/common';
import { Box, Stack, styled, ErrorDialog } from '@liscio/ui';
import { defaultErrorParser } from '@liscio/utils';

import {
  Copyright,
  Logo,
  MagicLinkSentView,
  MfaSetupDesktopOnlyInfo,
  SignInForm,
} from '../components';
import {
  LOGIN_EVENTS,
  LOGIN_PRIMARY_METHODS,
  type LoginPrimaryMethod,
  useTrackLoginEvent,
} from 'analytics/login/useTrackLoginEvent';
import { config } from 'config';
import {
  CpaLoginV5Selector,
  MfaCodeForm,
} from 'modules/authenticate/components';
import { useSetLoggedInUserSession } from 'modules/authenticate/hooks';

const SplashStyled = styled(Box)(({ theme }) => ({
  flex: 1,
  backgroundImage: `url(${theme.assets?.loginHero})`,
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
}));

const ProcessWrapperStyled = styled(Box)({
  flex: 1,
  padding: '16px',
  display: 'flex',
  justifyContent: 'center',
});

const ProcessInnerWrapperStyled = styled(Box)({
  width: '100%',
  maxWidth: '516px',
});

export const LoginV5Desktop = () => {
  const setLoggedUserSession = useSetLoggedInUserSession();
  const trackLoginEvent = useTrackLoginEvent();
  const hasTrackedScreenViewedRef = useRef(false);
  const signInTabInitial = useMemo(
    () => getSignInTabInitialState('password'),
    []
  );
  const loginPrimaryMethodRef = useRef<LoginPrimaryMethod>(
    LOGIN_PRIMARY_METHODS.PASSWORD
  );

  const authV5ProcessState = useAuthWithBiometryV5Process({
    onProcessFinished: async (loggedInUser) => {
      await setLoggedUserSession(loggedInUser);
    },
    webAppUrl: config.webApp.url,
    appType: 'firm',
    trackLoginEvent,
    loginPrimaryMethodRef,
  });

  const {
    processState,
    signInCpas,
    signIn,
    signInWithPasskey,
    isPasskeySupported,
    generateMagicLink,
    resetProcess,
    selectCpa,
    validateMfaCode,
    isLoading,
    error,
    getSelectedCpa,
    signInEmail,
  } = authV5ProcessState;

  useEffect(() => {
    if (hasTrackedScreenViewedRef.current) {
      return;
    }
    hasTrackedScreenViewedRef.current = true;
    trackLoginEvent(LOGIN_EVENTS.SCREEN_VIEWED, {
      initial_tab:
        signInTabInitial.tab === 'magicLink' ? 'magic_link' : 'password',
      initial_tab_source: signInTabInitial.source,
    });
  }, [signInTabInitial.source, signInTabInitial.tab, trackLoginEvent]);

  const {
    resetProcessWithAnalytics,
    handleSignInForm,
    handleSendMagicLink,
    handleMfaRecoveryMagicLink,
  } = useLoginV5ViewController({
    appType: 'firm',
    cpaSiteUrl: undefined,
    processState,
    signIn,
    generateMagicLink,
    resetProcess,
    trackLoginEvent,
    loginPrimaryMethodRef,
    includeBiometrics: true,
  });

  if (processState === 'cpaSelector') {
    return (
      <CpaLoginV5Selector
        cpas={signInCpas}
        userEmail={signInEmail}
        onCancel={resetProcessWithAnalytics}
        onCpaSelected={(selectedCpa) => {
          selectCpa(selectedCpa.cpa_uuid!);
        }}
      />
    );
  }

  if (processState === 'mfaSetup') {
    return (
      <MfaSetupDesktopOnlyInfo
        resetProcess={resetProcessWithAnalytics}
        getSelectedCpa={getSelectedCpa}
      />
    );
  }

  if (processState === 'magicLinkSent') {
    return (
      <MagicLinkSentView
        email={signInEmail}
        onBackToLogin={resetProcessWithAnalytics}
      />
    );
  }

  return (
    <AuthWithBiometryV5ProcessProvider
      authWithBiometryV5ProcessState={authV5ProcessState}
    >
      <Stack height="100vh" sx={{ backgroundColor: 'background.default' }}>
        <Stack flex={1} direction="row">
          <ProcessWrapperStyled>
            <ProcessInnerWrapperStyled>
              <Logo />
              {processState === 'loginForm' && (
                <SignInForm
                  isLoading={isLoading}
                  onSubmit={handleSignInForm}
                  onSendMagicLink={handleSendMagicLink}
                  onSignInWithPasskey={signInWithPasskey}
                  isPasskeySupported={isPasskeySupported}
                />
              )}
              {processState === 'mfaCode' && (
                <MfaCodeForm
                  backToLogin={resetProcessWithAnalytics}
                  isLoading={isLoading}
                  onSubmit={({ mfaCode }) => validateMfaCode(mfaCode)}
                  onRequestMagicLink={handleMfaRecoveryMagicLink(signInEmail)}
                />
              )}
              {processState === 'cpaSelected' && <LoadingPlaceholderShared />}
            </ProcessInnerWrapperStyled>
          </ProcessWrapperStyled>
          <SplashStyled />
        </Stack>
        <Copyright variant="bar" />
        <ErrorDialog
          title="Sign In Error"
          error={error}
          parseError={defaultErrorParser}
        />
      </Stack>
    </AuthWithBiometryV5ProcessProvider>
  );
};

export default LoginV5Desktop;
